<script setup>
import { ref, defineProps, defineEmits, onMounted, onUnmounted, watch } from 'vue';
import SelectInput from '@/components/forms/inputs/select-input.vue';
import TextInput from '@/components/forms/inputs/text-input.vue';
import { validateReferenceFile } from '@/pages/dataset/services/eval/evalBuilderValidationService';
import { useToast } from '@/composables/use-toast.js';
import { useEvals } from '@/composables/use-evals';

const { addToast } = useToast();
const { 
    evalStatus,
    evalError,
    initializeEvalBuilder,
    updateEvalBuilderField,
    clearPreviewEvalPayload
} = useEvals();

const props = defineProps({
    selectedFile: {
        type: Object,
        default: null
    },
    isUploading: {
        type: Boolean,
        default: false
    },
    isLoadingDataset: {
        type: Boolean,
        default: false
    },
    isSubmitting: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:selectedFile', 'openFormatModal']);

// Load preview data from localStorage if available
const loadPreviewFromStorage = () => {
    try {
        const storedPreview = localStorage.getItem('preview_eval_payload');
        if (storedPreview) {
            const previewData = JSON.parse(storedPreview);
            
            // Load tasks if exists
            if (previewData.tasks && Array.isArray(previewData.tasks) && previewData.tasks.length > 0) {
                return previewData.tasks.map((task, index) => ({
                    id: index + 1,
                    taskPrompt: task.prompt || '',
                    answer: task.answer || '',
                    referenceFile: null,
                    collapsed: index !== 0, // Only first task expanded
                    criteria: task.criteria || [],
                    tools: task.tools || [],
                    passThreshold: task.passThreshold || 70
                }));
            }
        }
    } catch (error) {
        console.error('Error loading preview from localStorage:', error);
    }
    return null;
};

const evalBuilderTasks = ref(loadPreviewFromStorage() || [
    {
        id: 1,
        taskPrompt: '',
        answer: '',
        referenceFile: null,
        collapsed: false
    }
]);

// Store field errors for each task
const fieldErrors = ref({});

const accepetedFileFormats = ref([]);

const getAcceptedFileFormats = async () => {
    const response = await getAcceptedFileFormats();
    if(response.error){
        addToast(response.message, 'error');
    }
    accepetedFileFormats.value = response.data;
}


let taskIdCounter = 2;
const activeTaskId = ref(1);

const addNewTask = () => {

    const activeTask = evalBuilderTasks.value.find(t => t.id === activeTaskId.value);
    if (activeTask) {
        const taskPromptEmpty = !activeTask.taskPrompt || activeTask.taskPrompt.trim() === '';
        const answerEmpty = !activeTask.answer || activeTask.answer.trim() === '';

        // Initialize field errors for this task if not exists
        if (!fieldErrors.value[activeTask.id]) {
            fieldErrors.value[activeTask.id] = {
                taskPrompt: false,
                answer: false
            };
        }

        // Set field errors for validation
        fieldErrors.value[activeTask.id].taskPrompt = taskPromptEmpty;
        fieldErrors.value[activeTask.id].answer = answerEmpty;

        if (taskPromptEmpty || answerEmpty) {
            const missingFields = [];
            if (taskPromptEmpty) missingFields.push('task prompt');
            if (answerEmpty) missingFields.push('answer');

            const fieldText = missingFields.length === 1
                ? missingFields[0]
                : missingFields.slice(0, -1).join(', ') + ' and ' + missingFields[missingFields.length - 1];

            addToast(`Please fill in the ${fieldText} before adding a new task.`, 'error');
            return;
        }
    }

    // Collapse all existing tasks when adding a new one
    for (const task of evalBuilderTasks.value) {
        task.collapsed = true;
    }

    const newTaskId = taskIdCounter++;
    evalBuilderTasks.value.push({
        id: newTaskId,
        taskPrompt: '',
        answer: '',
        referenceFile: null,
        collapsed: false
    });
    activeTaskId.value = newTaskId;

    // Send task added update via WebSocket
    updateEvalBuilderField('taskAdded', {
        taskId: newTaskId,
        totalTasks: evalBuilderTasks.value.length
    }, 'task_added', true); // Send immediately
};

const setActiveTask = (taskId) => {
    activeTaskId.value = taskId;
};

const removeTask = (taskId) => {
    if (evalBuilderTasks.value.length > 1) {
        const taskToRemove = evalBuilderTasks.value.find(t => t.id === taskId);
        const wasActive = activeTaskId.value === taskId;
        
        // check if the file of the removed task is still being used in other tasks and remove if not
        const fileName = taskToRemove?.referenceFile?.name || selectedFileOption.value[taskId];
        checkAndRemoveFileIfUnused(fileName, taskId);
        
        // remove the file selection from the removed task
        delete selectedFileOption.value[taskId];
        
        evalBuilderTasks.value = evalBuilderTasks.value.filter(task => task.id !== taskId);
        if (wasActive && evalBuilderTasks.value.length > 0) {
            activeTaskId.value = evalBuilderTasks.value[0].id;
        }

        // Send task removed update via WebSocket
        updateEvalBuilderField('taskRemoved', {
            taskId,
            totalTasks: evalBuilderTasks.value.length
        }, 'task_removed', true); // Send immediately
        
        // Clear preview from storage when task is deleted
        clearPreviewEvalPayload();
    }
};

const handleReferenceFileUpload = async (event, taskId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if file with the same name already exists in available files
    if (availableFiles.value.has(file.name)) {
        addToast(`File "${file.name}" already exists in available files. Use the "Select File" field to reuse it.`, 'error');
        event.target.value = '';
        return;
    }

    try {
        await validateReferenceFile(file);
    }
    catch (error) {
        console.error('Error validating reference file:', error);
        addToast(error.message || 'Error validating reference file', 'error');
        event.target.value = '';
        return;
    }

    const task = evalBuilderTasks.value.find(t => t.id === taskId);
    if (task) {
        task.referenceFile = file;
        selectedFileOption.value[taskId] = null;
        
        // add the file to the available files map for other tasks
        availableFiles.value.set(file.name, file);
        updateFileSelectOptions();
                
        // Send file upload update via WebSocket
        updateEvalBuilderField('referenceFileUploaded', {
            taskId,
            fileName: file.name
        }, 'reference_file_uploaded', true); // Send immediately
    }
};

const handleClearReferenceFile = (event, taskId) => {
    event.stopPropagation();
    const task = evalBuilderTasks.value.find(t => t.id === taskId);
    if (task) {
        const fileName = task.referenceFile?.name || selectedFileOption.value[taskId];
        task.referenceFile = null;
        selectedFileOption.value[taskId] = null;
        
        // reset the file input
        const fileInput = document.getElementById(`reference-file-${taskId}`);
        if (fileInput) {
            fileInput.value = '';
        }
        
        // check if the file is still being used in other tasks and remove if not
        checkAndRemoveFileIfUnused(fileName, taskId);
                
        // Send file cleared update via WebSocket
        updateEvalBuilderField('referenceFileCleared', {
            taskId,
            fileName
        }, 'reference_file_cleared', true); // Send immediately
    }
};

// Helper function to check if file is still in use and remove it if not
const checkAndRemoveFileIfUnused = (fileName, excludeTaskId) => {
    if (!fileName) return;
    
    const isFileStillInUse = evalBuilderTasks.value.some(
        t => t.id !== excludeTaskId && (
            t.referenceFile?.name === fileName || 
            selectedFileOption.value[t.id] === fileName
        )
    );
    
    // if file is not being used in other tasks, remove it from the available files
    if (!isFileStillInUse) {
        availableFiles.value.delete(fileName);
        updateFileSelectOptions();
    }
};

const selectReferenceFile = (taskId) => {
    const fileInput = document.getElementById(`reference-file-${taskId}`);
    if (fileInput) {
        fileInput.click();
    }
};

// store the available files for selection (key: file name, value: File object)
const availableFiles = ref(new Map());

// generate the select options based on the available files
const fileSelectOptions = ref([]);

const updateFileSelectOptions = () => {
  fileSelectOptions.value = Array.from(availableFiles.value.entries()).map(([fileName, file]) => ({
    value: fileName,
    label: fileName
  }));
};

const selectedFileOption = ref({});

const handleFileOptionSelect = (taskId) => {
  const task = evalBuilderTasks.value.find(t => t.id === taskId);
  if (task) {
    if (!selectedFileOption.value[taskId] || selectedFileOption.value[taskId] === '') {
      // Get the file name before clearing
      const fileName = task.referenceFile?.name || selectedFileOption.value[taskId];
      
      selectedFileOption.value[taskId] = null;
      task.referenceFile = null;
      
      // reset the file input
      const fileInput = document.getElementById(`reference-file-${taskId}`);
      if (fileInput) {
        fileInput.value = '';
      }
      
      // check if the file is still being used in other tasks and remove if not
      checkAndRemoveFileIfUnused(fileName, taskId);
      
      return;
    }
    
    // when selecting from the select, assign the file but keep the reference that it was selected from the select
    const selectedFileName = selectedFileOption.value[taskId];
    const selectedFile = availableFiles.value.get(selectedFileName);
    
    if (selectedFile) {
      task.referenceFile = selectedFile;
      // reset the file input
      const fileInput = document.getElementById(`reference-file-${taskId}`);
      if (fileInput) {
        fileInput.value = '';
      }
    }
  }
};


const isFileFromSelect = (taskId) => {
  return !!selectedFileOption.value[taskId];
};

const getTaskPreview = (taskPrompt) => {
    if (!taskPrompt || taskPrompt.trim() === '') {
        return 'No task prompt yet...';
    }
    const maxLength = 80;
    if (taskPrompt.length <= maxLength) {
        return taskPrompt;
    }
    return taskPrompt.substring(0, maxLength) + '...';
};

const getReferenceFileName = (taskId) => {
    const task = evalBuilderTasks.value.find(t => t.id === taskId);
    if (!task) return null;
    
    if (task.referenceFile) {
        return task.referenceFile.name;
    }
    
    if (selectedFileOption.value[taskId]) {
        return selectedFileOption.value[taskId];
    }
    
    return null;
};

// Method to set field errors from validation
const setFieldErrors = (errors) => {
    fieldErrors.value = errors || {};
};

// Method to clear field errors for a specific task
const clearTaskErrors = (taskId) => {
    if (fieldErrors.value[taskId]) {
        fieldErrors.value[taskId] = {
            taskPrompt: false,
            answer: false
        };
    }
};

// Clear errors when user starts typing
const handleTaskPromptInput = (taskId) => {
    if (fieldErrors.value[taskId]?.taskPrompt) {
        fieldErrors.value[taskId].taskPrompt = false;
    }
    setActiveTask(taskId);
        
    // Send field update via WebSocket (debounced)
    const task = evalBuilderTasks.value.find(t => t.id === taskId);
    if (task) {
        updateEvalBuilderField('taskPrompt', {
            taskId,
            value: task.taskPrompt
        }, 'task_prompt_updated', true); // Send immediately
    }
};

const handleAnswerInput = (taskId) => {
    if (fieldErrors.value[taskId]?.answer) {
        fieldErrors.value[taskId].answer = false;
    }
    setActiveTask(taskId);
    
    // Send field update via WebSocket (debounced)
    const task = evalBuilderTasks.value.find(t => t.id === taskId);
    if (task) {
        updateEvalBuilderField('answer', {
            taskId,
            value: task.answer
        }, 'answer_updated', true); // Send immediately
    }
};

// Watch for eval status changes
watch(evalStatus, (newStatus) => {
    if (newStatus === 'error' && evalError.value) {
        addToast(evalError.value, 'error');
    } else if (newStatus === 'created') {
        addToast('Eval preview created successfully', 'success');
    } else if (newStatus === 'completed') {
        addToast('Eval completed successfully', 'success');
    }
});

// Initialize WebSocket connection on mount
onMounted(() => {
    const cleanup = initializeEvalBuilder();
    
    // Store cleanup function for unmount
    onUnmounted(() => {
        cleanup();
    });
});

defineExpose({
    evalBuilderTasks,
    setFieldErrors
});
</script>

<template>
    <div class="flex flex-col h-full">
        <div class="flex flex-col gap-4 mb-6">
            <div v-for="(task, index) in evalBuilderTasks" :key="task.id"
                class="border rounded-xl bg-echoes-background-gray/30 cursor-pointer"
                :class="{
                    'border-echoes-primary': activeTaskId === task.id,
                    'border-echoes-border-gray': activeTaskId !== task.id
                }"
                
                @click="setActiveTask(task.id)">

                <div class="flex items-center justify-between p-4 bg-echoes-background-gray/20">
                    <button @click.stop="task.collapsed = !task.collapsed"
                        class="flex items-center justify-between w-full text-left bg-transparent border-none cursor-pointer p-0">
                        <div class="flex gap-3 flex-1 min-w-0" :class="task.collapsed && !getReferenceFileName(task.id) ? 'items-center' : 'items-start'">
                            <div class="flex items-center gap-2 flex-shrink-0">
                                <span class="text-white font-semibold text-base">Task {{ index + 1 }}</span>
                                <i class="uil text-white/70 text-lg flex-shrink-0" 
                                   :class="task.collapsed ? 'uil-angle-down' : 'uil-angle-up'"></i>
                            </div>
                            <div v-if="task.collapsed" class="flex flex-col gap-2 flex-1 min-w-0 ml-2">
                                <span class="text-white/60 text-sm truncate">{{ getTaskPreview(task.taskPrompt) }}</span>
                                <span v-if="getReferenceFileName(task.id)" 
                                      class="flex items-center gap-1.5 text-white/50 text-xs flex-shrink-0 px-2 py-1 rounded-md bg-echoes-background-black/50 w-fit">
                                    <i class="uil uil-file text-xs"></i>
                                    <span class="truncate max-w-[120px]">{{ getReferenceFileName(task.id) }}</span>
                                </span>
                            </div>
                        </div>
                    </button>
                    <button v-if="evalBuilderTasks.length > 1" 
                            @click.stop="removeTask(task.id)"
                            class="ml-3 text-lg h-7 w-7 flex items-center justify-center text-white/70 hover:text-red-500 hover:bg-red-500/10 rounded-md flex-shrink-0">
                        <i class="uil uil-times"></i>
                    </button>
                </div>

    
                <div v-if="!task.collapsed" class="p-4 pt-4 flex flex-col gap-4 bg-echoes-background-gray/10">
                    <!-- Task Prompt -->
                    <div class="w-full">
                        <TextInput v-model="task.taskPrompt" :id="`task-prompt-${task.id}`"
                            placeholder="Write your task here..." :isTextarea="true" :rows="4"
                            :borderClass="fieldErrors[task.id]?.taskPrompt ? 'border-red-500' : 'border-echoes-border-gray'"
                            @input="handleTaskPromptInput(task.id)" />
                    </div>

                    <!-- Answer -->
                    <div class="w-full">
                        <TextInput v-model="task.answer" :id="`task-answer-${task.id}`"
                            placeholder="Write the answer here..." :isTextarea="true" :rows="4"
                            :borderClass="fieldErrors[task.id]?.answer ? 'border-red-500' : 'border-echoes-border-gray'"
                            @input="handleAnswerInput(task.id)" />
                    </div>

                    <!-- Reference File Upload/Select -->
                    <div class="w-full flex flex-col gap-2 p-3 rounded-xl bg-echoes-background-black/50">
                        
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
                            <label :for="selectedFileOption[task.id] ? null : `reference-file-${task.id}`"
                                class="flex items-center justify-between px-4 py-2.5 rounded-[13px] h-11 bg-echoes-background-black border border-echoes-border-gray hover:bg-echoes-background-black hover:border-white/20 hover:text-white focus:border-echoes-primary w-full"
                                :class="{ 
                                    'text-white': task.referenceFile, 
                                    'text-white/50': !task.referenceFile,
                                    'cursor-pointer': !selectedFileOption[task.id],
                                    'cursor-not-allowed opacity-50': selectedFileOption[task.id]
                                }"
                                @click="(e) => {
                                    if (selectedFileOption[task.id]) {
                                        e.preventDefault();
                                        return;
                                    }
                                    setActiveTask(task.id);
                                }">
                                <div class="flex items-center gap-2 flex-1 pr-2 overflow-hidden">
                                    <i class="uil text-lg"
                                        :class="task.referenceFile && !isFileFromSelect(task.id) ? 'uil-file' : 'uil-upload'"></i>
                                    <span class="text-xs md:text-sm flex items-center truncate">
                                        {{ task.referenceFile && !isFileFromSelect(task.id) ? task.referenceFile.name : 'Upload File' }}
                                    </span>
                                </div>
                                <div class="flex items-center gap-1 flex-shrink-0">
                                    <button
                                        v-if="task.referenceFile && !isFileFromSelect(task.id)"
                                        type="button"
                                        class="rounded-full hover:bg-echoes-background-gray/50"
                                        @click.stop="handleClearReferenceFile($event, task.id)"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 text-white/80">
                                            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                                        </svg>
                                    </button>
                                </div>
                            </label>
                            <input type="file" :id="`reference-file-${task.id}`"
                                :disabled="!!selectedFileOption[task.id]"
                                @change="handleReferenceFileUpload($event, task.id)" style="display: none;" />
                            <div @click.stop="setActiveTask(task.id)" class="relative">
                                <SelectInput :id="`file-select-${task.id}`" v-model="selectedFileOption[task.id]"
                                    :options="fileSelectOptions" placeholder="Select File"
                                    @update:modelValue="handleFileOptionSelect(task.id)" :hideSearch="false"
                                    :disabled="!!task.referenceFile && !isFileFromSelect(task.id)" :clearable="true"
                                    style="width: 100%;" />
                            </div>
                            <span class="text-white/80 text-sm mb-1">
                                Select or upload your reference file (optional)
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Task Button -->
        <div class="flex justify-center mt-2">
            <button @click="addNewTask" class="flex items-center justify-center bg-echoes-primary hover:bg-echoes-primary/80 transition-colors ease-in-out duration-200 rounded-full h-[35px] w-[35px]">
                <i class="uil uil-plus"></i>
            </button>
        </div>
    </div>
</template>
